import React from "react";
import { X, Check } from "lucide-react";
import { SectionStepper } from "../SectionStepper";
import PreviewBanner from "../PreviewBanner";
import NavigationBar from "../NavigationBar";
import { SignatureModal } from "../SignatureModal";

export interface SectionInfo { id: string; name: string; html: string }

interface AcroWithSectionHtmlProps {
  sections: SectionInfo[];
  currentSectionIndex: number;
  onSelectSection: (idx: number) => void;
  isPublishedForm: boolean;
  errorText: string | null;
  showSuccess: boolean;
  mutationIsPending: boolean;
  submitFormPending: boolean;
  publishFormPending: boolean;
  isSubmitEnabled: boolean;
  onSaveChanges: () => void;
  onSubmitForm: () => void;
  onPublishForm: () => void;
  // Signature modal props
  sigOpen: boolean;
  signatureValue: string;
  onCloseSignature: () => void;
  onChangeSignature: (val: string) => void;
  onSaveSignature: (signature: string) => void;
  // Navigation + banner
  isFirst: boolean;
  isLast: boolean;
  currentLabel: string;
  onPrevious: () => void;
  onNext: () => void;
  sectionCount: number;
  subsectionCount: number;
  fieldCount: number;
  isSaveDisabled?: boolean; // disable save/submit when member is not assigned
}

export const AcroWithSectionHtml: React.FC<AcroWithSectionHtmlProps> = ({
  sections,
  currentSectionIndex,
  onSelectSection,
  isPublishedForm,
  errorText,
  showSuccess,
  mutationIsPending,
  submitFormPending,
  publishFormPending,
  isSubmitEnabled,
  onSaveChanges,
  onSubmitForm,
  onPublishForm,
  sigOpen,
  signatureValue,
  onCloseSignature,
  onChangeSignature,
  onSaveSignature,
  isFirst,
  isLast,
  currentLabel,
  onPrevious,
  onNext,
  sectionCount,
  subsectionCount,
  fieldCount,
  isSaveDisabled = false,
}) => {

  // Custom logic: if only one header and one section, merge and hide stepper
  const isHeaderStep = sections.length === 2 && sections[0].name === "header" && sections[1].name !== "header";
  const onlyOneHeaderAndSection = isHeaderStep;

  return (
    <>
      {!onlyOneHeaderAndSection && sections.length > 1 && (
        <SectionStepper
          sections={sections.map((s) => ({ id: s.id, name: s.name }))}
          currentIndex={currentSectionIndex}
          onSelect={onSelectSection}
        />
      )}

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-end gap-3">
          {errorText && (
            <span className="error-badge" aria-live="assertive">
              <span className="error-badge__icon" aria-hidden="true">
                <X className="w-3 h-3" />
              </span>
              <span className="text-sm text-red-600 truncate max-w-[200px]">
                {errorText}
              </span>
            </span>
          )}
          {showSuccess && (
            <span className="success-badge" aria-live="polite">
              <span className="success-badge__icon" aria-hidden="true">
                <Check className="w-3 h-3" />
              </span>
              <span className="text-sm text-green-600">Saved</span>
            </span>
          )}
          {isPublishedForm && (
            <>
              <button
                type="button"
                onClick={onSaveChanges}
                disabled={isSaveDisabled || mutationIsPending || submitFormPending || publishFormPending}
                className={`px-4 py-1.5 text-sm rounded-full transition ${isSaveDisabled || mutationIsPending || submitFormPending || publishFormPending
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "btn-secondary"
                  }`}
              >
                {mutationIsPending ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                onClick={onSubmitForm}
                disabled={isSaveDisabled || !isSubmitEnabled || mutationIsPending || submitFormPending || publishFormPending}
                className={`px-4 py-1.5 text-sm rounded-full transition ${isSaveDisabled || !isSubmitEnabled || mutationIsPending || submitFormPending || publishFormPending
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "btn-primary"
                  }`}
              >
                {submitFormPending ? "Submitting..." : "Submit Form"}
              </button>
            </>
          )}
          {!isPublishedForm && (
            <button
              type="button"
              onClick={onPublishForm}
              disabled={mutationIsPending || submitFormPending || publishFormPending}
              className={`px-4 py-1.5 text-sm rounded-full transition ${mutationIsPending || submitFormPending || publishFormPending
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "btn-primary"
                }`}
            >
              {publishFormPending ? "Publishing..." : "Publish Form"}
            </button>
          )}
        </div>
      </div>

      <div className="flex-auto overflow-y-auto px-6 py-6">
        <div className="max-w-6xl mx-auto bg-white border border-gray-200 rounded-lg p-4">
          <div
            className="prose max-w-none preview-form-content preview-acro-form flex flex-col gap-4"
            dangerouslySetInnerHTML={{
              __html: onlyOneHeaderAndSection
                ? sections[0].html + sections[1].html
                : sections[currentSectionIndex].html
            }}
          />
        </div>
      </div>

      <SignatureModal
        isOpen={sigOpen}
        value={signatureValue}
        onClose={onCloseSignature}
        onChange={onChangeSignature}
        onSave={onSaveSignature}
      />

      <NavigationBar
        isFirst={isFirst}
        isLast={isLast}
        currentLabel={currentLabel}
        onPrevious={onPrevious}
        onNext={onNext}
      />
      <PreviewBanner
        sectionCount={sectionCount}
        subsectionCount={subsectionCount}
        fieldCount={fieldCount}
      />
    </>
  );
};
